import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Star, ShieldCheck, MessageSquare } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COPY = {
  en: {
    heading: 'Verified researcher reviews',
    no_reviews: 'No reviews yet, be the first verified buyer to leave one.',
    based_on: (n) => `Based on ${n} verified review${n === 1 ? '' : 's'}`,
    sort: { recent: 'Most recent', highest: 'Highest rated', lowest: 'Lowest rated' },
    verified: 'Verified purchase',
    show_more: 'Show more reviews',
    distribution: 'Rating distribution',
  },
  es: {
    heading: 'Reseñas de investigadores verificados',
    no_reviews: 'Aún no hay reseñas, sé el primer comprador verificado en dejar una.',
    based_on: (n) => `Basado en ${n} reseña${n === 1 ? '' : 's'} verificada${n === 1 ? '' : 's'}`,
    sort: { recent: 'Más recientes', highest: 'Mejor valoradas', lowest: 'Peor valoradas' },
    verified: 'Compra verificada',
    show_more: 'Mostrar más reseñas',
    distribution: 'Distribución de valoraciones',
  },
  ru: {
    heading: 'Отзывы проверенных исследователей',
    no_reviews: 'Пока отзывов нет, станьте первым проверенным покупателем.',
    based_on: (n) => `Основано на ${n} проверенн${n === 1 ? 'ом отзыве' : 'ых отзывах'}`,
    sort: { recent: 'Новые', highest: 'Высокие оценки', lowest: 'Низкие оценки' },
    verified: 'Проверенная покупка',
    show_more: 'Показать ещё',
    distribution: 'Распределение оценок',
  },
  de: {
    heading: 'Bewertungen verifizierter Forscher',
    no_reviews: 'Noch keine Bewertungen, seien Sie der erste verifizierte Käufer.',
    based_on: (n) => `Basierend auf ${n} verifizierten Bewertung${n === 1 ? '' : 'en'}`,
    sort: { recent: 'Neueste', highest: 'Beste Bewertung', lowest: 'Schlechteste Bewertung' },
    verified: 'Verifizierter Kauf',
    show_more: 'Mehr Bewertungen anzeigen',
    distribution: 'Bewertungsverteilung',
  },
  fr: {
    heading: 'Avis de chercheurs vérifiés',
    no_reviews: 'Aucun avis pour le moment, soyez le premier acheteur vérifié à en laisser un.',
    based_on: (n) => `Basé sur ${n} avis vérifié${n === 1 ? '' : 's'}`,
    sort: { recent: 'Plus récents', highest: 'Mieux notés', lowest: 'Moins bien notés' },
    verified: 'Achat vérifié',
    show_more: "Afficher plus d'avis",
    distribution: 'Répartition des notes',
  },
  it: {
    heading: 'Recensioni di ricercatori verificati',
    no_reviews: 'Nessuna recensione ancora, sii il primo acquirente verificato a lasciarne una.',
    based_on: (n) => `Basato su ${n} recensione verificata${n === 1 ? '' : ', '}`,
    sort: { recent: 'Più recenti', highest: 'Valutazione più alta', lowest: 'Valutazione più bassa' },
    verified: 'Acquisto verificato',
    show_more: 'Mostra altre recensioni',
    distribution: 'Distribuzione delle valutazioni',
  },
  pl: {
    heading: 'Recenzje zweryfikowanych badaczy',
    no_reviews: 'Brak recenzji, bądź pierwszym zweryfikowanym kupującym, który ją wystawi.',
    based_on: (n) => `Na podstawie ${n} zweryfikowanej recenzji`,
    sort: { recent: 'Najnowsze', highest: 'Najwyżej oceniane', lowest: 'Najniżej oceniane' },
    verified: 'Zakup zweryfikowany',
    show_more: 'Pokaż więcej recenzji',
    distribution: 'Rozkład ocen',
  },
  nl: {
    heading: 'Beoordelingen van geverifieerde onderzoekers',
    no_reviews: 'Nog geen beoordelingen, wees de eerste geverifieerde koper die er een achterlaat.',
    based_on: (n) => `Gebaseerd op ${n} geverifieerde beoordeling${n === 1 ? '' : 'en'}`,
    sort: { recent: 'Meest recent', highest: 'Hoogst beoordeeld', lowest: 'Laagst beoordeeld' },
    verified: 'Geverifieerde aankoop',
    show_more: 'Meer beoordelingen tonen',
    distribution: 'Beoordelingsverdeling',
  },
  pt: {
    heading: 'Avaliações de pesquisadores verificados',
    no_reviews: 'Ainda não há avaliações, seja o primeiro comprador verificado a deixar uma.',
    based_on: (n) => `Com base em ${n} avaliação verificada${n === 1 ? '' : 's'}`,
    sort: { recent: 'Mais recentes', highest: 'Melhor avaliadas', lowest: 'Pior avaliadas' },
    verified: 'Compra verificada',
    show_more: 'Mostrar mais avaliações',
    distribution: 'Distribuição das avaliações',
  },
  au: {
    heading: 'Verified researcher reviews',
    no_reviews: 'No reviews yet, be the first verified buyer to leave one.',
    based_on: (n) => `Based on ${n} verified review${n === 1 ? '' : 's'}`,
    sort: { recent: 'Most recent', highest: 'Highest rated', lowest: 'Lowest rated' },
    verified: 'Verified purchase',
    show_more: 'Show more reviews',
    distribution: 'Rating distribution',
  },
};

function StarRow({ value, size = 'sm' }) {
  const px = size === 'sm' ? 'w-4 h-4' : 'w-5 h-5';
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((n) => (
        <Star
          key={n}
          className={`${px} ${n <= Math.round(value) ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'}`}
        />
      ))}
    </div>
  );
}

export default function ProductReviews({ productId }) {
  const { currentLanguage } = useLanguage();
  const t = COPY[currentLanguage] || COPY.en;

  const [summary, setSummary] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [sort, setSort] = useState('recent');
  const [skip, setSkip] = useState(0);
  const [hasMore, setHasMore] = useState(false);

  const load = useCallback(async () => {
    try {
      const [s, list] = await Promise.all([
        axios.get(`${API}/products/${productId}/reviews/summary`),
        axios.get(`${API}/products/${productId}/reviews`, {
          params: { sort, skip: 0, limit: 6 },
        }),
      ]);
      setSummary(s.data);
      setReviews(list.data.reviews);
      setHasMore(list.data.reviews.length === 6 && list.data.reviews.length < (s.data.count || 0));
      setSkip(list.data.reviews.length);
    } catch (e) {
      // Silent fail, section just stays empty.
    }
  }, [productId, sort]);

  useEffect(() => {
    load();
  }, [load]);

  const loadMore = async () => {
    try {
      const res = await axios.get(`${API}/products/${productId}/reviews`, {
        params: { sort, skip, limit: 6 },
      });
      setReviews((prev) => [...prev, ...res.data.reviews]);
      setSkip(skip + res.data.reviews.length);
      setHasMore(res.data.reviews.length === 6 && skip + res.data.reviews.length < (summary?.count || 0));
    } catch (e) {}
  };

  if (!summary) return null;
  const distMax = Math.max(1, ...Object.values(summary.distribution || {}));

  return (
    <section
      className="bg-white border border-gray-200 rounded-xl p-5 sm:p-6 mt-2"
      data-testid="product-reviews-section"
    >
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <MessageSquare className="w-5 h-5 text-teal-600" />
          <h2 className="text-lg font-semibold text-gray-900">{t.heading}</h2>
        </div>
        {summary.count > 0 && (
          <select
            value={sort}
            onChange={(e) => setSort(e.target.value)}
            className="text-sm border border-gray-200 rounded-md px-2 py-1 bg-white"
            data-testid="reviews-sort"
          >
            <option value="recent">{t.sort.recent}</option>
            <option value="highest">{t.sort.highest}</option>
            <option value="lowest">{t.sort.lowest}</option>
          </select>
        )}
      </header>

      {summary.count === 0 ? (
        <p className="text-gray-500 mt-4 text-sm" data-testid="reviews-empty-state">{t.no_reviews}</p>
      ) : (
        <>
          <div className="mt-5 grid sm:grid-cols-2 gap-6">
            <div>
              <div className="flex items-baseline gap-3">
                <div className="text-4xl font-bold text-gray-900" data-testid="reviews-average">
                  {summary.average.toFixed(1)}
                </div>
                <StarRow value={summary.average} size="md" />
              </div>
              <div className="text-sm text-gray-500 mt-1" data-testid="reviews-count">
                {t.based_on(summary.count)}
              </div>
            </div>

            <div>
              <div className="text-xs text-gray-500 mb-1">{t.distribution}</div>
              {[5, 4, 3, 2, 1].map((star) => {
                const c = summary.distribution?.[String(star)] || 0;
                const pct = (c / distMax) * 100;
                return (
                  <div key={star} className="flex items-center gap-2 text-xs my-1">
                    <span className="w-3 text-gray-600">{star}</span>
                    <Star className="w-3.5 h-3.5 text-yellow-400 fill-yellow-400" />
                    <div className="flex-1 h-2 bg-gray-100 rounded">
                      <div className="h-full bg-yellow-400 rounded" style={{ width: `${pct}%` }} />
                    </div>
                    <span className="w-6 text-right text-gray-500">{c}</span>
                  </div>
                );
              })}
            </div>
          </div>

          <ul className="mt-6 space-y-4" data-testid="reviews-list">
            {reviews.map((r) => (
              <li
                key={r.id}
                className="bg-gray-50 border border-gray-100 rounded-lg p-4"
                data-testid={`review-card-${r.id}`}
              >
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2">
                    <StarRow value={r.rating} />
                    <h3 className="font-semibold text-gray-900 truncate">{r.title}</h3>
                  </div>
                  {r.verified_purchase && (
                    <span className="inline-flex items-center gap-1 text-[11px] px-1.5 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">
                      <ShieldCheck className="w-3 h-3" />
                      {t.verified}
                    </span>
                  )}
                </div>
                <p className="text-sm text-gray-700 mt-2 whitespace-pre-line">{r.text}</p>
                <div className="text-xs text-gray-500 mt-2 flex items-center gap-2">
                  <span className="font-medium text-gray-700">{r.reviewer_name}</span>
                  <span>·</span>
                  <span>{new Date(r.created_at).toLocaleDateString()}</span>
                </div>
                {r.admin_response && (
                  <div className="mt-3 border-l-2 border-teal-500 pl-3 text-sm text-gray-700 italic">
                    {r.admin_response}
                  </div>
                )}
              </li>
            ))}
          </ul>

          {hasMore && (
            <button
              onClick={loadMore}
              className="mt-4 text-sm font-semibold text-teal-700 hover:text-teal-800"
              data-testid="reviews-show-more"
            >
              {t.show_more}
            </button>
          )}
        </>
      )}
    </section>
  );
}
